import React from 'react';
import { cn } from '@/lib/utils';

interface SimpleMetricProps {
  emoji: string;
  value: string | number;
  unit?: string;
  label?: string;
  color?: 'default' | 'success' | 'warning' | 'destructive' | 'info';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const colorMap = {
  default: 'text-foreground',
  success: 'text-success',
  warning: 'text-warning',
  destructive: 'text-destructive',
  info: 'text-info',
};

const sizeMap = {
  sm: { emoji: 'text-2xl', value: 'text-2xl', unit: 'text-sm' },
  md: { emoji: 'text-3xl', value: 'text-3xl', unit: 'text-base' },
  lg: { emoji: 'text-4xl', value: 'text-5xl', unit: 'text-lg' },
};

/**
 * SimpleMetric — emoji + large number for Simple Mode (non-reading farmers).
 * 70% visual (emoji + color), 20% numbers, 10% text.
 */
export const SimpleMetric: React.FC<SimpleMetricProps> = ({
  emoji,
  value,
  unit,
  label,
  color = 'default',
  size = 'md',
  className,
}) => {
  const s = sizeMap[size];
  return (
    <div
      className={cn(
        'flex items-center gap-3 min-h-12 p-3 rounded-xl bg-muted/40',
        className
      )}
    >
      <span className={cn(s.emoji, 'shrink-0')}>{emoji}</span>
      <div className="flex items-baseline gap-1.5">
        <span className={cn(s.value, 'font-bold', colorMap[color])}>
          {value}
        </span>
        {unit && (
          <span className={cn(s.unit, 'text-muted-foreground font-medium')}>
            {unit}
          </span>
        )}
      </div>
      {label && (
        <span className="text-xs text-muted-foreground ml-auto simple-hide">
          {label}
        </span>
      )}
    </div>
  );
};

/**
 * SimpleMetricGrid — a responsive grid for multiple SimpleMetric items.
 */
export const SimpleMetricGrid: React.FC<{
  children: React.ReactNode;
  columns?: 2 | 3 | 4;
  className?: string;
}> = ({ children, columns = 2, className }) => (
  <div
    className={cn(
      'grid gap-3',
      columns === 2 && 'grid-cols-2',
      columns === 3 && 'grid-cols-3',
      columns === 4 && 'grid-cols-2 sm:grid-cols-4',
      className
    )}
  >
    {children}
  </div>
);

export default SimpleMetric;
